import { SxProps, Theme } from '@mui/material/styles';

export const marketingBodyFont = 'var(--font-sans)';
export const marketingDisplayFont = 'var(--font-display)';

export const marketingPalette = {
  ink: '#0f172a',
  headline: '#10264d',
  body: '#475569',
  muted: '#64748b',
  subtle: '#94a3b8',
  slate: '#1e293b',
  border: '#dbe4ef',
  borderStrong: '#c4d4e8',
  surface: '#ffffff',
  surfaceAlt: '#f6f8fc',
  accent: '#0f6fff',
  accentDark: '#0b57d0',
  accentSoft: 'rgba(15,111,255,0.08)',
  accentBorder: 'rgba(15,111,255,0.18)',
  accentStrong: '#0ea5e9',
  accentGlow: '#7dd3fc',
  success: '#047857',
  successSoft: '#ecfdf5',
  successBorder: '#a7f3d0',
  darkSurface: '#10264d',
};

export const marketingSectionSx: SxProps<Theme> = {
  py: { xs: 5.5, md: 7 },
  position: 'relative',
  overflow: 'hidden',
};

export const marketingMutedSectionSx: SxProps<Theme> = {
  ...marketingSectionSx,
  background:
    'radial-gradient(ellipse 90% 55% at 50% -5%, rgba(15,111,255,0.07), transparent 58%), linear-gradient(180deg, #e8f0fa 0%, #eef4fb 40%, #f4f8fd 100%)',
};

export const marketingDarkSectionSx: SxProps<Theme> = {
  ...marketingSectionSx,
  background:
    'radial-gradient(ellipse 65% 50% at 15% 40%, rgba(15,111,255,0.22), transparent 55%), radial-gradient(ellipse 55% 40% at 85% 15%, rgba(14,165,233,0.13), transparent 50%), radial-gradient(ellipse 40% 35% at 60% 80%, rgba(124,58,237,0.08), transparent 45%), linear-gradient(160deg, #060d1a 0%, #0a1628 40%, #0d1e3c 70%, #07101f 100%)',
};

export const marketingCardSx: SxProps<Theme> = {
  p: { xs: 2.25, md: 2.6 },
  height: '100%',
  position: 'relative',
  overflow: 'hidden',
  border: '1px solid',
  borderColor: 'rgba(196,212,232,0.96)',
  borderRadius: 1.25,
  boxShadow: '0 1px 2px rgba(15,23,42,0.04), 0 6px 16px rgba(15,23,42,0.05), 0 20px 40px rgba(15,23,42,0.04)',
  background:
    'linear-gradient(180deg, rgba(255,255,255,0.985) 0%, rgba(250,252,255,0.985) 100%)',
  transition: 'transform 240ms ease, box-shadow 240ms ease, border-color 240ms ease',
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    background:
      'radial-gradient(circle at top right, rgba(15,111,255,0.09), transparent 30%), linear-gradient(135deg, rgba(255,255,255,0.32), rgba(255,255,255,0))',
    pointerEvents: 'none',
  },
  '&:hover': {
    transform: 'translateY(-3px)',
    borderColor: 'rgba(15,111,255,0.26)',
    boxShadow: '0 2px 4px rgba(15,111,255,0.04), 0 10px 28px rgba(15,111,255,0.10), 0 28px 56px rgba(15,23,42,0.08)',
  },
};

export const marketingSoftCardSx: SxProps<Theme> = {
  ...marketingCardSx,
  backgroundColor: '#fbfdff',
};

export const marketingPrimaryButtonSx: SxProps<Theme> = {
  borderRadius: '8px',
  background: 'linear-gradient(135deg, #0f6fff 0%, #1488ff 100%)',
  color: 'white',
  px: 3,
  py: 1.5,
  fontWeight: 400,
  fontSize: '0.95rem',
  textTransform: 'none',
  boxShadow: '0 16px 32px rgba(15,111,255,0.24)',
  '&:hover': {
    background: 'linear-gradient(135deg, #0b57d0 0%, #0f6fff 100%)',
    boxShadow: '0 20px 40px rgba(15,111,255,0.28)',
  },
};

export const marketingSecondaryButtonSx: SxProps<Theme> = {
  borderRadius: '8px',
  borderColor: marketingPalette.borderStrong,
  color: marketingPalette.ink,
  px: 3,
  py: 1.5,
  fontWeight: 400,
  fontSize: '0.95rem',
  textTransform: 'none',
  backgroundColor: 'rgba(255,255,255,0.88)',
  backdropFilter: 'blur(10px)',
  '&:hover': {
    borderColor: marketingPalette.accentBorder,
    backgroundColor: '#f8fbff',
  },
};

export const marketingPillSx: SxProps<Theme> = {
  display: 'inline-flex',
  alignItems: 'center',
  borderRadius: '7px',
  border: '1px solid',
  borderColor: marketingPalette.accentBorder,
  backgroundColor: marketingPalette.accentSoft,
  color: marketingPalette.accent,
  px: 1.55,
  py: 0.78,
  fontSize: '0.75rem',
  fontWeight: 400,
  letterSpacing: '0.02em',
  lineHeight: 1,
};

export const marketingIconListRowSx: SxProps<Theme> = {
  position: 'relative',
  paddingLeft: '20px',
  minHeight: '1.7em',
};

export const marketingIconListMarkerSx: SxProps<Theme> = {
  width: 8,
  height: 8,
  position: 'absolute',
  left: 0,
  top: '0.52em',
  borderRadius: 1.5,
  backgroundColor: marketingPalette.accent,
};

export const marketingDotListRowSx: SxProps<Theme> = {
  position: 'relative',
  paddingLeft: '18px',
  minHeight: '1.7em',
};

export const marketingDotListMarkerWrapSx: SxProps<Theme> = {
  width: 12,
  height: 12,
  position: 'absolute',
  left: 0,
  top: '9px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  lineHeight: 0,
  flexShrink: 0,
};

export const marketingDotListMarkerSx: SxProps<Theme> = {
  width: 7,
  height: 7,
  borderRadius: '50%',
  backgroundColor: marketingPalette.accent,
};

export const marketingHeroSectionSx: SxProps<Theme> = {
  position: 'relative',
  width: '100%',
  overflow: 'hidden',
  background:
    'radial-gradient(circle at top right, rgba(219,234,254,0.78), transparent 28%), radial-gradient(circle at left center, rgba(191,219,254,0.2), transparent 24%), linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)',
  py: { xs: 7, md: 10 },
};

export const marketingHeroGridSx: SxProps<Theme> = {
  pointerEvents: 'none',
  position: 'absolute',
  inset: 0,
  background:
    'radial-gradient(circle at 80% 14%, rgba(15,111,255,0.42), transparent 20%), radial-gradient(circle at 16% 24%, rgba(14,165,233,0.28), transparent 18%), radial-gradient(circle at 68% 58%, rgba(191,219,254,0.32), transparent 24%), radial-gradient(circle at 50% 80%, rgba(124,58,237,0.06), transparent 20%), linear-gradient(120deg, rgba(15,111,255,0.13), rgba(255,255,255,0) 40%), linear-gradient(to right, rgba(148,163,184,0.038) 1px, transparent 1px), linear-gradient(to bottom, rgba(148,163,184,0.026) 1px, transparent 1px)',
  backgroundSize: 'auto, auto, auto, auto, auto, 96px 96px, 96px 96px',
  maskImage: 'linear-gradient(180deg, rgba(0,0,0,0.98), rgba(0,0,0,0.35))',
};

export const marketingFinalBandSx: SxProps<Theme> = {
  py: { xs: 6.5, md: 8.25 },
  background:
    'radial-gradient(circle at 14% 18%, rgba(125,211,252,0.12), transparent 22%), radial-gradient(circle at top right, rgba(96,165,250,0.22), transparent 28%), linear-gradient(135deg, #0c1d3f 0%, #10264d 40%, #15366c 72%, #0f2851 100%)',
  position: 'relative',
  overflow: 'hidden',
};

export const marketingFinalCardSx: SxProps<Theme> = {
  position: 'relative',
  overflow: 'hidden',
  textAlign: 'left',
  p: { xs: 3.4, md: 4.2 },
  borderRadius: 1.25,
  border: '1px solid rgba(147,197,253,0.16)',
  background: 'linear-gradient(180deg, rgba(255,255,255,0.075), rgba(255,255,255,0.02))',
  boxShadow: '0 24px 62px rgba(2,6,23,0.22)',
  backdropFilter: 'blur(18px)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 1,
    background: 'linear-gradient(90deg, rgba(125,211,252,0), rgba(125,211,252,0.75), rgba(125,211,252,0))',
  },
};

export const marketingFinalSecondaryButtonSx: SxProps<Theme> = {
  ...marketingSecondaryButtonSx,
  borderColor: 'rgba(226,232,240,0.92)',
  borderWidth: '1.5px',
  color: marketingPalette.headline,
  backgroundColor: 'rgba(248,250,252,0.96)',
  boxShadow: '0 16px 34px rgba(2,6,23,0.18)',
  backdropFilter: 'blur(10px)',
  '&:hover': {
    borderColor: 'rgba(255,255,255,1)',
    borderWidth: '1.5px',
    backgroundColor: 'rgba(255,255,255,1)',
    color: marketingPalette.ink,
    boxShadow: '0 18px 36px rgba(2,6,23,0.22)',
  },
};

export const marketingFooterLinkSx: SxProps<Theme> = {
  color: 'rgba(255,255,255,0.82)',
  fontSize: '0.9rem',
  textDecoration: 'none',
  '&:hover': {
    color: 'white',
  },
};

export const buildIconBadgeSx = (backgroundColor: string, color: string): SxProps<Theme> => ({
  width: 52,
  height: 52,
  borderRadius: 2.5,
  backgroundColor,
  color,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.4)',
});
